import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Select,
  Textarea,
  Textfield,
} from "../../../../components/core";
import { Divider } from "primereact/divider";
import { Card } from "primereact/card";
import { ContentContainer } from "../../../../components/layout";
import { Strings } from "../../../../constants/strings";
import { Dialog } from "primereact/dialog";
import PartnerAccessPermissionDialog from "./access-permission-configuration";
import { setLockerTypeList } from "../../../../features/locker-fees/edit-partner-fees-slice";

const PartnerProfileForm = ({
  isEditing = false,
  partnerProfileData,
  partnerCategoryItems,
  partnerTypesItems,
  partnerCategory,
  contactPersonFields,
  paymentSchedulesItem,
  changesMade,
  handleOnChange,
  handleResetFeatures,
  handleSaveChanges,
  handleCancelChanges,
  setValidation,
  onSubmit,
  onCancel,
  isEditingPaymentScheduleNo,
  setActiveIndex,
  isAlreadyExisting,
  goToNextStep,
  accessPermission,
  partnerId,
  ...props
}) => {
  const hasEditPermission =
    accessPermission.partners === "2" || accessPermission.partners === 2;

  const containerTitle = isEditing
    ? hasEditPermission
      ? "EDIT PARTNER PROFILE"
      : "VIEW PARTNER PROFILE"
    : "ADD PARTNER PROFILE";
  const submitButtonText = "Save Changes";
  const cancelButtonText = "Cancel";

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    } else {
      handleValidateFields();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_confirm_cancel_title,
        message: Strings.popup_confirm_cancel_massage,
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"No"}
              className={"p-2 w-full"}
            />
            <Button
              onClick={handleCancelChanges}
              value={"Yes"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    }
  };

  const showValidationError = (message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: Strings.popup_error_title,
      message,
      open: true,
      actions: (
        <div className="grid grid-cols-1 gap-2">
          <Button
            value={"OK"}
            className={"p-2"}
            onClick={() => setValidation(false)}
          />
        </div>
      ),
    });
  };

  const handleValidateFields = () => {
    const updatedData = { ...partnerProfileData };
    let emailRegex = /^[a-zA-Z0-9._%+-ñÑ]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Simple validation checks with early returns
    if (updatedData.partnerCategoryNo === "-1")
      return showValidationError("Partner Category is a required field");

    if (updatedData.partnerTypeId === "-1")
      return showValidationError("Partner Type is a required field");

    if (updatedData.companyNickname === "")
      return showValidationError("Partner Name is a required field");

    if (updatedData.zipCode === "")
      return showValidationError("Zip Code is a required field");

    if (updatedData.tinNumber === "")
      return showValidationError("TIN Number is a required field");
    if (updatedData.tinNumber !== "" && updatedData.tinNumber.length < 12)
      return showValidationError("TIN must be 12 characters long");

    if (updatedData.contactPerson === "" || updatedData.contactPerson2 === "")
      return showValidationError("Contact Name is a required field");

    if (updatedData.contactPosition === "")
      return showValidationError("Contact Position is a required field");
    if (updatedData.mobile !== "" || updatedData.mobile2 !== "") {
      if (updatedData.mobile.length < 10 || updatedData.mobile2.length < 10) {
        return showValidationError("Please enter a valid mobile number");
      }
      if (
        !updatedData.mobile.startsWith("9") ||
        !updatedData.mobile2.startsWith("9")
      ) {
        return showValidationError("Invalid mobile number format");
      }
    } else {
      return showValidationError("Contact Mobile is a required field");
    }

    if (updatedData.email === "" || updatedData.email2 === "")
      return showValidationError("Contact Email is a required field");

    if (
      (updatedData.email !== "" && !emailRegex.test(updatedData.email)) ||
      (updatedData.email2 !== "" && !emailRegex.test(updatedData.email2))
    )
      return showValidationError("Please provide a valid email address");

    // Check if required first
    if (updatedData.landline === "" || updatedData.landline2 === "")
      return showValidationError("Landline is a required field");

    // Then validate length
    if (updatedData.landline.length < 10 || updatedData.landline.length > 11)
      return showValidationError("Landline must be 10-11 characters");
    if (updatedData.landline2.length < 10 || updatedData.landline2.length > 11)
      return showValidationError("Landline must be 10-11 characters");

    if (updatedData.paymentScheduleNo === "")
      return showValidationError("Payment Plan is a required field");
    else {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
        title: Strings.popup_confirm_save_title,
        message: Strings.popup_confirm_save_massage,
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"No"}
              className={"p-2 w-full"}
            />
            <Button
              onClick={handleSaveChanges}
              value={"Yes"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    }
  };

  return (
    <ContentContainer containerTitle={containerTitle} {...props}>
      <div className="grid grid-cols-8">
        <div className="col-span-8">
          <div className="grid grid-cols-12 items-center space-y-2">
            {/* Partner Category */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Partner Category:
            </div>
            {isEditing ? (
              <p className="font-bold col-span-6">{partnerCategory}</p>
            ) : (
              <div className="col-span-6">
                <Select
                  items={partnerCategoryItems}
                  defaultOption={{
                    value: "-1",
                    name: "-- Select Partner Category --",
                  }}
                  name="partnerCategoryNo"
                  value={partnerProfileData.partnerCategoryNo}
                  onChange={handleOnChange}
                />
              </div>
            )}
            <div className="col-span-4"></div>

            {/* Partner Type */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Partner Type:
            </div>
            <div className="col-span-6">
              <Select
                items={partnerTypesItems}
                defaultOption={{
                  value: "-1",
                  name: "-- Select Partner Type --",
                }}
                name="partnerTypeId"
                disabled={
                  partnerProfileData.partnerCategoryNo === "-1" ||
                  accessPermission.partners === "1"
                }
                value={partnerProfileData.partnerTypeId}
                onChange={handleOnChange}
              />
            </div>
            <div className="col-span-4"></div>

            {/* Partner Name */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Partner Name:
            </div>
            <div className="col-span-6">
              <Textfield
                maxLength={86}
                placeholder="Enter company/partner name"
                name="companyNickname"
                value={partnerProfileData.companyNickname}
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>

            {/* TIN Number */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>TIN Number:
            </div>
            <div className="col-span-6">
              <Textfield
                maxLength={12}
                placeholder="XXXXXXXXXXXX"
                name="tinNumber"
                isNumberOnly={true}
                value={partnerProfileData.tinNumber}
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>

            {/* Address */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Address:
            </div>
            <div className="col-span-6">
              <Textarea
                placeholder="Enter complete address"
                name="address"
                maxLength={255}
                value={partnerProfileData.address}
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>

            {/* Zip Code */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Zip code:
            </div>
            <div className="col-span-6">
              <Textfield
                placeholder="XXXX"
                maxLength={4}
                isNumberOnly={true}
                name="zipCode"
                value={partnerProfileData.zipCode}
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>
          </div>
        </div>

        {/* Divider */}
        <div className="col-span-8 px-4">
          <Divider className="border border-gray-200" />
        </div>
        {/* Contact Persons Section */}
        <div className="col-span-8">
          <div className="mb-4">
            <h2 className="text-[17px] font-bold">Contact Persons</h2>
          </div>

          {contactPersonFields.map((contactPerson) => (
            <div
              key={contactPerson.index}
              className="border border-gray-300 rounded-lg p-4 mb-4 bg-gray-50"
            >
              <div className="mb-3">
                <div className="font-semibold">
                  Contact Person {contactPerson.index}
                </div>
              </div>

              <div className="grid grid-cols-12 space-y-2 items-center">
                {/* Contact Name */}
                <div className="col-span-2">
                  <i className="text-red-500 mr-2">*</i>Contact Name:
                </div>
                <div className="col-span-6">
                  <Textfield
                    maxLength={86}
                    placeholder="Enter contact name"
                    name={contactPerson.fields.contactPerson}
                    value={
                      partnerProfileData[contactPerson.fields.contactPerson]
                    }
                    onChange={handleOnChange}
                    disabled={
                      accessPermission.partners === "1" ||
                      accessPermission.partners === 1
                    }
                  />
                </div>
                <div className="col-span-4"></div>

                {/* Contact Position */}
                <div className="col-span-2">
                  <i className="text-red-500 mr-2">*</i>Contact Position:
                </div>
                <div className="col-span-6">
                  <Textfield
                    placeholder="Enter contact position"
                    maxLength={86}
                    name={contactPerson.fields.contactPosition}
                    value={
                      partnerProfileData[contactPerson.fields.contactPosition]
                    }
                    onChange={handleOnChange}
                    disabled={
                      accessPermission.partners === "1" ||
                      accessPermission.partners === 1
                    }
                  />
                </div>
                <div className="col-span-4"></div>

                {/* Mobile Number */}
                <div className="col-span-2">
                  <i className="text-red-500 mr-2">*</i>Mobile Number:
                </div>
                <div className="col-span-1 text-center font-medium">+63</div>
                <div className="col-span-5">
                  <Textfield
                    isNumberOnly={true}
                    maxLength={10}
                    placeholder="Enter mobile number"
                    name={contactPerson.fields.mobile}
                    value={partnerProfileData[contactPerson.fields.mobile]}
                    onChange={handleOnChange}
                    disabled={
                      accessPermission.partners === "1" ||
                      accessPermission.partners === 1
                    }
                  />
                </div>
                <div className="col-span-4"></div>

                {/* Email */}
                <div className="col-span-2">
                  <i className="text-red-500 mr-2">*</i>Email:
                </div>
                <div className="col-span-6">
                  <Textfield
                    placeholder="Enter email address"
                    type="email"
                    name={contactPerson.fields.email}
                    value={partnerProfileData[contactPerson.fields.email]}
                    onChange={handleOnChange}
                    disabled={
                      accessPermission.partners === "1" ||
                      accessPermission.partners === 1
                    }
                  />
                </div>
                <div className="col-span-4"></div>

                {/* Landline */}
                <div className="col-span-2">
                  <i className="text-red-500 mr-2">*</i>Landline:
                </div>
                <div className="col-span-6">
                  <Textfield
                    maxLength={11}
                    placeholder="Enter landline number"
                    name={contactPerson.fields.landline}
                    value={partnerProfileData[contactPerson.fields.landline]}
                    onChange={handleOnChange}
                    disabled={
                      accessPermission.partners === "1" ||
                      accessPermission.partners === 1
                    }
                  />
                </div>
                <div className="col-span-4"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="col-span-8 px-4">
          <Divider className="border border-gray-200" />
        </div>

        {/* Payment Plans and Features Section */}
        <div className="col-span-8">
          <div className="grid grid-cols-12 items-center space-y-2">
            {/* Payment Plan */}
            <div className="col-span-2">
              <i className="text-red-500 mr-2">*</i>Payment Plan:
            </div>
            <div className="col-span-6">
              <Select
                items={paymentSchedulesItem}
                defaultOption={{
                  value: "-1",
                  name: "-- Select Payment Plan --",
                }}
                name="paymentScheduleNo"
                value={
                  isEditing
                    ? isEditingPaymentScheduleNo
                    : partnerProfileData.paymentScheduleNo
                }
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>

            {/* Enabled Features */}
            <Card className="col-span-6 bg-primary-primaryYellowLight">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-[16px] text-secondary-purpleGray">
                  Enabled Features
                </div>
                <u
                  className={`${
                    accessPermission.partners === 1 ||
                    accessPermission.partners === "1"
                      ? "hidden"
                      : "flex hover:text-blue-500 cursor-pointer ml-auto w-min"
                  }`}
                  onClick={handleResetFeatures}
                >
                  <i className="fas fa-undo my-auto"></i>
                  <p className="ml-1">Reset</p>
                </u>
              </div>
              <div className="col-span-5 text-secondary-purpleGray">
                <Checkbox
                  label="Acceptance of Payment on Delivery (POD) at Locker"
                  checked={partnerProfileData.featuresCod}
                  onChange={handleOnChange}
                  name="featuresCod"
                  disabled={
                    accessPermission.partners === "1" ||
                    accessPermission.partners === 1
                  }
                />
                <Checkbox
                  label="Shipout service"
                  checked={partnerProfileData.featuresShipout}
                  onChange={handleOnChange}
                  name="featuresShipout"
                  disabled={
                    accessPermission.partners === "1" ||
                    accessPermission.partners === 1
                  }
                />
                <Checkbox
                  label="Accepts returns"
                  checked={partnerProfileData.featuresReturns}
                  onChange={handleOnChange}
                  name="featuresReturns"
                  disabled={
                    accessPermission.partners === "1" ||
                    accessPermission.partners === 1
                  }
                />
              </div>
            </Card>
          </div>
        </div>

        {/* Divider */}
        <div className="col-span-8 px-4">
          <Divider className="border border-gray-200" />
        </div>

        {/* Remarks Section */}
        <div className="col-span-8">
          <div className="grid grid-cols-12 items-center space-y-2">
            <div className="col-span-2">
              {/* <i className="text-red-500 mr-2">*</i> */}
              Remarks:
            </div>
            <div className="col-span-6">
              <Textarea
                name="remarks"
                placeholder="Enter remarks"
                value={partnerProfileData.remarks}
                onChange={handleOnChange}
                disabled={
                  accessPermission.partners === "1" ||
                  accessPermission.partners === 1
                }
              />
            </div>
            <div className="col-span-4"></div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="col-span-8 pt-4">
          <div className={`grid grid-cols-8 gap-2`}>
            <div
              className={`${isAlreadyExisting ? "hidden" : "col-span-2"}`}
            ></div>
            <div className="col-span-8 flex w-full justify-between">
              <div></div>

              {isAlreadyExisting && hasEditPermission && (
                <div>
                  <span className="text-[14px] text-component-underlineBlue italic">
                    Save changes first before proceeding to next page
                  </span>
                </div>
              )}
            </div>
            <>
              <Button
                type="button"
                disabled={
                  !changesMade ||
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                }
                value={cancelButtonText}
                className="p-2 col-span-2"
                onClick={handleCancel}
              />
              <Button
                type="submit"
                // disabled={!changesMade}
                disabled={
                  !changesMade ||
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                }
                value={submitButtonText}
                className="p-2 col-span-2"
                onClick={handleSubmit}
              />
            </>
            <div
              className={`${
                // isAlreadyExisting ||
                // accessPermission.partners === 1 ||
                // accessPermission.partners === "1"
                // ?
                "items-end justify-end flex col-span-4"
                // : "hidden"
              }`}
            >
              <Button
                type="button"
                disabled={changesMade}
                className={`${
                  partnerId === "new" ? "hidden" : ""
                } p-2 col-span-2 bg-primary-primaryYellowLight`}
                // onClick={handleCancel}
                value="Next Page"
                size="small"
                iconPos="right"
                icon="pi pi-angle-double-right"
                onClick={() => {
                  // handleEditOP();
                  goToNextStep();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </ContentContainer>
  );
};

export default PartnerProfileForm;
